import i18next from 'i18next'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { WidgetFontSelectDropdown } from '../../constructs'
import { Glyphs, WidgetInputToggleField, WidgetMicroMenu, WidgetSelectGroup } from '../../rapid-ui'
import {
	storyAdMetaAttributionPositionAtom,
	storyAdMetaGeneralContentFontFamilyOverrideAtom,
	storyAdMetaGeneralContentHideAtom,
	storyAdMetaLocationPositionAtom,
	storyAdMetaLogoBackgroundFillColorAtom,
	storyAdMetaLogoPositionAtom,
	storyAdMetaStackOrientationAtom,
} from '../state'
import { trackLayerInteracted } from '../tracking'
import { EditorWidgetColorField } from './shared/editor-widget-color-field'

const positionOptions = {
	topLeft: {
		id: 'top-left',
		render: () => <Glyphs.core.LayoutTopLeftIcon />,
		label: i18next.t('editor:contentLayers.topLeft'),
	},
	topRight: {
		id: 'top-right',
		render: () => <Glyphs.core.LayoutTopRightIcon />,
		label: i18next.t('editor:contentLayers.topRight'),
	},
	bottomLeft: {
		id: 'bottom-left',
		render: () => <Glyphs.core.LayoutBottomLeftIcon />,
		label: i18next.t('editor:contentLayers.bottomLeft'),
	},
	bottomRight: {
		id: 'bottom-right',
		render: () => <Glyphs.core.LayoutBottomRightIcon />,
		label: i18next.t('editor:contentLayers.bottomRight'),
	},
	hide: {
		id: 'hide',
		render: () => <Glyphs.core.HideIcon />,
		label: i18next.t('editor:contentLayers.hide'),
	},
} as const

const logoOptionPositions = [
	positionOptions.topLeft,
	positionOptions.topRight,
	positionOptions.bottomLeft,
	positionOptions.bottomRight,
	positionOptions.hide,
]

const locationOptionPositions = [
	positionOptions.topLeft,
	positionOptions.topRight,
	positionOptions.bottomLeft,
	positionOptions.bottomRight,
	positionOptions.hide,
]

const attributionOptionPositions = [
	positionOptions.topRight,
	positionOptions.bottomRight,
	positionOptions.hide,
]

const stackOrientationOptionPositions = [
	{
		id: 'vertical',
		render: () => <>{i18next.t('editor:contentLayers.vertical')}</>,
		label: i18next.t('editor:contentLayers.vertical'),
	},
	{
		id: 'horizontal',
		render: () => <>{i18next.t('editor:contentLayers.horizontal')}</>,
		label: i18next.t('editor:contentLayers.horizontal'),
	},
]

export function StoryAdContentLayerControls() {
	const { t } = useTranslation('editor')
	const [logoPosition, setLogoPosition] = useAtom(storyAdMetaLogoPositionAtom)
	const [logoBackgroundFillColor, setLogoBackgroundFillColor] = useAtom(storyAdMetaLogoBackgroundFillColorAtom)
	const [locationPosition, setLocationPosition] = useAtom(storyAdMetaLocationPositionAtom)
	const [attributionPosition, setAttributionPosition] = useAtom(storyAdMetaAttributionPositionAtom)
	const [fontFamilyOverride, setFontFamilyOverride] = useAtom(storyAdMetaGeneralContentFontFamilyOverrideAtom)
	const [hide, setHide] = useAtom(storyAdMetaGeneralContentHideAtom)

	const [stackOrientation, setStackOrientation] = useAtom(storyAdMetaStackOrientationAtom)

	const hasEssentialState = logoPosition !== undefined
		&& logoBackgroundFillColor !== undefined
		&& locationPosition !== undefined
		&& attributionPosition !== undefined
		&& stackOrientation !== undefined

	if (!hasEssentialState) {
		return null
	}

	return (
		<>
			<div className="text-left ml-10 mb-4 grid grid-cols-[auto_1fr] items-center gap-2">
				<div className="text-xs text-grey-600">
					{t('contentLayers.showContent')}
				</div>

				<div className="">
					<WidgetInputToggleField
						value={!(hide ?? false)}
						onChange={(value) => {
							trackLayerInteracted('content_visibility_toggled', 'story')
							setHide(!value)
						}}
					/>
				</div>

				<div className="text-xs text-grey-600">
					{t('contentLayers.font')}
				</div>
				<div className="grid items-center gap-2 grid-cols-[1fr_auto]">
					<WidgetFontSelectDropdown
						value={fontFamilyOverride ?? ''}
						onChange={(value) => {
							trackLayerInteracted('font_changed', 'story')
							setFontFamilyOverride(value)
						}}
						allowDefault={true}
					/>
					<WidgetMicroMenu
						items={[
							{
								id: 'reset',
								label: t('contentLayers.resetToDefault'),
								onSelect: () => {
									trackLayerInteracted('font_reset', 'story')
									setFontFamilyOverride('')
								},
							},
						]}
					/>
				</div>

				<div className="text-xs text-grey-600">
					{t('contentLayers.logo')}
				</div>
				<WidgetSelectGroup
					value={logoPosition}
					onChange={(value) => {
						trackLayerInteracted('logo_position_changed', 'story')
						setLogoPosition(value)
					}}
					items={logoOptionPositions}
				/>
				{logoPosition !== 'hide' && (
					<>
						<div className="text-xs text-grey-600">
							{t('contentLayers.logoBackground')}
						</div>
						<EditorWidgetColorField
							value={logoBackgroundFillColor}
							onChange={(value) => {
								trackLayerInteracted('logo_background_color_changed', 'story')
								setLogoBackgroundFillColor(value)
							}}
							label={t('contentLayers.logoBackground')}
							includeTransparent
						/>
					</>
				)}
				<div className="text-xs text-grey-600">
					{t('contentLayers.location')}
				</div>
				<WidgetSelectGroup
					value={locationPosition}
					onChange={(value) => {
						trackLayerInteracted('location_position_changed', 'story')
						setLocationPosition(value)
					}}
					items={locationOptionPositions}
				/>
				<div className="text-xs text-grey-600">
					{t('contentLayers.attribution')}
				</div>
				<WidgetSelectGroup
					value={attributionPosition}
					onChange={(value) => {
						trackLayerInteracted('attribution_position_changed', 'story')
						setAttributionPosition(value)
					}}
					items={attributionOptionPositions}
				/>

				<div className="text-xs text-grey-600">
					{t('contentLayers.stackOrientation')}
				</div>
				<WidgetSelectGroup
					value={stackOrientation}
					onChange={(value) => {
						trackLayerInteracted('stack_orientation_changed', 'story')
						setStackOrientation(value as 'vertical' | 'horizontal')
					}}
					items={stackOrientationOptionPositions}
				/>
			</div>
		</>
	)
}
